/**
 * Master-only Answer24 connection panel — iter139 G.
 *
 * Mounts on the dealer detail page. Never rendered for dealers/resellers.
 *
 * Flow:
 *   1. GET /status → show token_set / connected pill + last_verified_at.
 *   2. PUT /token  → save location_id + PIT (write-only; masked after save).
 *   3. POST /verify-read → list calendars, populate picker. No side effects.
 *   4. POST /test-push  → create + auto-delete a hidden test contact.
 *   5. PATCH /calendar  → save the selected ghl_calendar_id.
 *   6. DELETE /token    → disconnect the dealer.
 *
 * The PIT token is NEVER returned from the API after save — we only ever
 * show "••••••••" and a "Replace token" toggle to enter a new one.
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import {
  Loader2, Save, Trash2, Eye, Send, RefreshCw, Link as LinkIcon,
  ShieldCheck, ShieldAlert, ShieldOff, KeyRound, CheckCircle2, XCircle,
} from "lucide-react";

export default function Answer24ConnectionPanel({ dealer }) {
  const { apiFetch } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(""); // "save" | "verify" | "push" | "cal" | "clear"

  // Editable state
  const [locationDraft, setLocationDraft] = useState("");
  const [pitDraft, setPitDraft] = useState("");
  const [showReplaceToken, setShowReplaceToken] = useState(false);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendar, setSelectedCalendar] = useState("");
  const [verifyOutcome, setVerifyOutcome] = useState(null); // {ok, error, hint, count}
  const [pushOutcome, setPushOutcome] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await apiFetch(`/admin/dealers/${dealer.id}/answer24/status`, { method: "GET" });
      const s = r?.data || {};
      setStatus(s);
      setLocationDraft(s.location_id || "");
      setSelectedCalendar(s.calendar_id || "");
      setShowReplaceToken(!s.token_set);
    } catch (e) {
      toast?.({ title: "Failed to load Answer24 status", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [dealer.id, apiFetch, toast]);

  useEffect(() => { load(); }, [load]);

  const saveToken = async () => {
    if (busy) return;
    if (!locationDraft.trim() || !pitDraft.trim()) {
      toast?.({ title: "Location ID and PIT token are both required.", variant: "destructive" });
      return;
    }
    setBusy("save");
    try {
      await apiFetch(`/admin/dealers/${dealer.id}/answer24/token`, {
        method: "PUT",
        data: { location_id: locationDraft.trim(), pit_token: pitDraft.trim() },
      });
      setPitDraft("");
      setShowReplaceToken(false);
      setVerifyOutcome(null);
      setPushOutcome(null);
      setCalendars([]);
      toast?.({ title: "Answer24 token saved (encrypted)." });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast?.({ title: String(msg), variant: "destructive" });
    } finally { setBusy(""); }
  };

  const verifyRead = async () => {
    if (busy) return;
    setBusy("verify");
    setVerifyOutcome(null);
    try {
      const r = await apiFetch(`/admin/dealers/${dealer.id}/answer24/verify-read`, { method: "POST" });
      const d = r?.data || {};
      setVerifyOutcome(d);
      if (d.ok) {
        setCalendars(d.calendars || []);
        toast?.({ title: `Read OK — ${d.raw_count} calendar${d.raw_count === 1 ? "" : "s"} found.` });
      } else {
        toast?.({
          title: `Read failed (${d.status || "network"}) — ${d.error || "unknown"}`,
          variant: "destructive",
        });
      }
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Verify failed";
      toast?.({ title: String(msg), variant: "destructive" });
    } finally { setBusy(""); }
  };

  const testPush = async () => {
    if (busy) return;
    if (!window.confirm(
      "This will create a hidden test contact in Answer24 tagged 'verified-demand-test' and " +
      "auto-delete it. Proceed?"
    )) return;
    setBusy("push");
    setPushOutcome(null);
    try {
      const r = await apiFetch(`/admin/dealers/${dealer.id}/answer24/test-push`, { method: "POST" });
      const d = r?.data || {};
      setPushOutcome(d);
      if (d.ok) {
        toast?.({ title: d.note || "Push OK." });
      } else {
        toast?.({
          title: `Push failed (${d.status || "network"}) — ${d.error || "unknown"}`,
          variant: "destructive",
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Push failed";
      toast?.({ title: String(msg), variant: "destructive" });
    } finally { setBusy(""); }
  };

  const saveCalendar = async (calId) => {
    if (busy) return;
    setBusy("cal");
    try {
      await apiFetch(`/admin/dealers/${dealer.id}/answer24/calendar`, {
        method: "PATCH",
        data: { calendar_id: calId || null },
      });
      setSelectedCalendar(calId || "");
      toast?.({ title: calId ? "Calendar linked." : "Calendar unlinked." });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Save failed";
      toast?.({ title: String(msg), variant: "destructive" });
    } finally { setBusy(""); }
  };

  const disconnect = async () => {
    if (busy) return;
    if (!window.confirm(
      `Disconnect Answer24 for "${dealer.name}"?\n\nThe token will be deleted. ` +
      `Location ID + calendar ID stay so you can reconnect with a new token.`
    )) return;
    setBusy("clear");
    try {
      await apiFetch(`/admin/dealers/${dealer.id}/answer24/token`, { method: "DELETE" });
      setPitDraft("");
      setShowReplaceToken(true);
      setCalendars([]);
      setVerifyOutcome(null);
      setPushOutcome(null);
      toast?.({ title: "Answer24 disconnected." });
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Disconnect failed";
      toast?.({ title: String(msg), variant: "destructive" });
    } finally { setBusy(""); }
  };

  if (loading || !status) {
    return (
      <div className="p-4 flex items-center gap-2 text-gray-600" data-testid="answer24-panel-loading">
        <Loader2 className="animate-spin" size={16} /> Loading Answer24 status…
      </div>
    );
  }

  // Status pill
  let pillColor, pillIcon, pillLabel;
  if (!status.token_set) {
    pillColor = "bg-gray-100 text-gray-700 border-gray-300";
    pillIcon = <ShieldOff size={13} />;
    pillLabel = "Not connected";
  } else if (status.last_verify_status === "ok") {
    pillColor = "bg-emerald-100 text-emerald-800 border-emerald-300";
    pillIcon = <ShieldCheck size={13} />;
    pillLabel = "Connected · verified";
  } else if ((status.last_verify_status || "").startsWith("err_")) {
    pillColor = "bg-red-100 text-red-800 border-red-300";
    pillIcon = <ShieldAlert size={13} />;
    pillLabel = `Token error (${status.last_verify_status.replace("err_", "HTTP ")})`;
  } else {
    pillColor = "bg-amber-100 text-amber-800 border-amber-300";
    pillIcon = <ShieldAlert size={13} />;
    pillLabel = "Token saved · not yet verified";
  }

  const lastV = status.last_verified_at
    ? new Date(status.last_verified_at).toLocaleString()
    : "Never";

  return (
    <section
      className="bg-white rounded-lg border border-gray-200 p-5 space-y-4"
      data-testid="answer24-connection-panel"
    >
      <header className="flex items-center gap-2 flex-wrap">
        <LinkIcon size={16} className="text-slate-500" />
        <h3 className="text-[15px] font-semibold text-gray-900">Answer24 Connection</h3>
        <span
          className={`inline-flex items-center gap-1.5 text-[11px] font-semibold border rounded-full px-2 py-0.5 ${pillColor}`}
          data-testid="answer24-status-pill"
        >
          {pillIcon} {pillLabel}
        </span>
        <span className="text-[11px] text-gray-500 ml-auto" data-testid="answer24-last-verified">
          Last verified: {lastV}
        </span>
      </header>
      <p className="text-[12px] text-gray-500">
        Per-sub-account Personal Integration Token. Master admin only — dealers never see
        these fields. Tokens are encrypted at rest and never returned to the browser after save.
        API base:{" "}
        <code className="text-[11px] bg-gray-50 border border-gray-200 rounded px-1 py-0.5">
          {status.api_base}
        </code>
      </p>

      {/* ── Location + Token ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="text-[12px] font-medium text-gray-700 mb-1 block">
            Location ID (Answer24 sub-account)
          </span>
          <input
            type="text"
            className="input w-full font-mono text-[12px]"
            placeholder="e.g. GVzcqK6ArV9Ax7ZQqXYZ"
            value={locationDraft}
            onChange={(e) => setLocationDraft(e.target.value)}
            data-testid="answer24-location-id"
          />
        </label>
        <label className="block">
          <span className="text-[12px] font-medium text-gray-700 mb-1 flex items-center gap-1.5">
            <KeyRound size={12} /> PIT Token
          </span>
          {!showReplaceToken && status.token_set ? (
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="input w-full font-mono text-[12px] bg-gray-50"
                value="••••••••••••••••••••••••"
                readOnly
                data-testid="answer24-pit-masked"
              />
              <button
                type="button"
                onClick={() => setShowReplaceToken(true)}
                className="text-[12px] font-semibold text-blue-700 hover:text-blue-900 whitespace-nowrap"
                data-testid="answer24-replace-token-btn"
              >
                Replace token
              </button>
            </div>
          ) : (
            <input
              type="password"
              className="input w-full font-mono text-[12px]"
              placeholder="pit-••••-••••-••••…"
              value={pitDraft}
              onChange={(e) => setPitDraft(e.target.value)}
              autoComplete="new-password"
              data-testid="answer24-pit-token"
            />
          )}
        </label>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          onClick={saveToken}
          disabled={busy !== "" || !locationDraft.trim() || !pitDraft.trim()}
          className="btn-primary flex items-center gap-2 text-[12px]"
          data-testid="answer24-save-token"
        >
          {busy === "save" ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
          Save token
        </button>
        <button
          type="button"
          onClick={verifyRead}
          disabled={busy !== "" || !status.token_set}
          className="btn-secondary flex items-center gap-2 text-[12px]"
          data-testid="answer24-verify-read"
          title="Fetch the calendar list from Answer24. Zero side effects."
        >
          {busy === "verify" ? <Loader2 size={13} className="animate-spin" /> : <Eye size={13} />}
          Verify Read
        </button>
        <button
          type="button"
          onClick={testPush}
          disabled={busy !== "" || !status.token_set}
          className="btn-secondary flex items-center gap-2 text-[12px]"
          data-testid="answer24-test-push"
          title="Create + auto-delete a hidden test contact. Proves push permissions work."
        >
          {busy === "push" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          Send Test Push
        </button>
        {status.token_set && (
          <button
            type="button"
            onClick={disconnect}
            disabled={busy !== ""}
            className="ml-auto flex items-center gap-1.5 text-[12px] font-semibold text-red-700 hover:text-red-900 disabled:opacity-50"
            data-testid="answer24-disconnect"
          >
            {busy === "clear" ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
            Disconnect
          </button>
        )}
      </div>

      {/* ── Verify outcome banner ── */}
      {verifyOutcome && (
        <div
          className={`text-[12px] rounded-md p-2.5 border ${
            verifyOutcome.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-red-50 border-red-200 text-red-900"
          }`}
          data-testid="answer24-verify-outcome"
        >
          <div className="flex items-center gap-1.5 font-semibold">
            {verifyOutcome.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            {verifyOutcome.ok
              ? `Read OK — ${verifyOutcome.raw_count} calendar${verifyOutcome.raw_count === 1 ? "" : "s"} available.`
              : `Read failed — HTTP ${verifyOutcome.status || "?"}: ${verifyOutcome.error}`}
          </div>
          {!verifyOutcome.ok && verifyOutcome.hint && (
            <div className="mt-1 text-[11px]">{verifyOutcome.hint}</div>
          )}
        </div>
      )}

      {/* ── Push outcome banner ── */}
      {pushOutcome && (
        <div
          className={`text-[12px] rounded-md p-2.5 border ${
            pushOutcome.ok
              ? "bg-emerald-50 border-emerald-200 text-emerald-900"
              : "bg-red-50 border-red-200 text-red-900"
          }`}
          data-testid="answer24-push-outcome"
        >
          <div className="flex items-center gap-1.5 font-semibold">
            {pushOutcome.ok ? <CheckCircle2 size={13} /> : <XCircle size={13} />}
            {pushOutcome.ok
              ? pushOutcome.note || "Push OK."
              : `Push failed — HTTP ${pushOutcome.status || "?"}: ${pushOutcome.error}`}
          </div>
          {!pushOutcome.ok && pushOutcome.hint && (
            <div className="mt-1 text-[11px]">{pushOutcome.hint}</div>
          )}
        </div>
      )}

      {/* ── Calendar picker (populated by Verify Read) ── */}
      {calendars.length > 0 && (
        <div className="border-t border-gray-100 pt-4">
          <label className="block text-[12px]">
            <span className="font-medium text-gray-700 mb-1 flex items-center gap-1.5">
              <RefreshCw size={12} /> Test Drive calendar for this dealer
            </span>
            <select
              className="input w-full text-[12px]"
              value={selectedCalendar}
              onChange={(e) => saveCalendar(e.target.value)}
              disabled={busy !== ""}
              data-testid="answer24-calendar-picker"
            >
              <option value="">— No calendar (fall back to business hours) —</option>
              {calendars.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.isActive ? "" : " (inactive)"}
                </option>
              ))}
            </select>
            <span className="text-[11px] text-gray-500 mt-1 block">
              When set, the booking popup pulls real availability from this Answer24 calendar.
              When empty, we synthesize slots from this dealer&apos;s business hours.
            </span>
          </label>
        </div>
      )}
      {status.calendar_id && calendars.length === 0 && (
        <div className="border-t border-gray-100 pt-3 text-[11px] text-gray-500">
          Linked calendar:{" "}
          <code className="font-mono">{status.calendar_id}</code> · Click{" "}
          <strong>Verify Read</strong> to refresh the picker.
        </div>
      )}
    </section>
  );
}
